import React, { Component, ReactNode, ChangeEvent } from 'react';
import './ChangeAvatar.scss';
import Button from 'react-bootstrap/Button';
import { Api } from '../../api';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { readAndCompressImage } from 'browser-image-resizer';
import { Icon } from '../icon/Icon';

interface ChangeAvatarProps {
  userId?: number;
  avatarUrl?: string;
  className?: string;
  onChangeAvatar?: (avatarUrl?: string) => void;
}

interface ChangeAvatarState {
  avatarUrl: string;
  uploading?: boolean;
}

class ChangeAvatar extends Component<ChangeAvatarProps, ChangeAvatarState> {
  fileBrowserRef = React.createRef<HTMLInputElement>();

  state: ChangeAvatarState = {
    avatarUrl: '/assets/avatar_placeholder.png'
  };

  componentDidMount(): void {
    // get avatar url and set
    this.loadAvatarUrl()
      .catch(err => console.error('Avatar load failed', err));
  }

  private async loadAvatarUrl(): Promise<void> {
    const { userId, avatarUrl } = this.props;
    if (avatarUrl) {
      this.setState({ avatarUrl });
    } else if (userId) {
      const blob = await Api.getAvatar(userId);
      const url = URL.createObjectURL(blob);
      this.setState({ avatarUrl: url });
    }
  }

  handleClick = (): void => {
    const fileBrowser = this.fileBrowserRef.current;
    if (fileBrowser) {
      fileBrowser.click();
    } else {
      console.error('File browser opened while component not fully loaded.');
    }
  };

  handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();
    const uploadElement = this.fileBrowserRef.current;
    const fileList = uploadElement?.files;
    if (uploadElement && fileList && fileList.length !== 0) {
      this.setState({ uploading: true });
      const file = fileList[0]; // eslint-disable-line prefer-destructuring
      const formData = new FormData();
      let newAvatar: Blob;
      this.resizeFile(file)
        .then(convertedFile => {
          newAvatar = convertedFile;
          formData.append('document', convertedFile);
          return Api.uploadAvatar(formData);
        })
        .then(() => {
          let url: string|undefined = undefined;
          if (newAvatar) {
            url = URL.createObjectURL(newAvatar);
            this.setState({ avatarUrl: url });
          }
          if (this.props.onChangeAvatar) this.props.onChangeAvatar(url);
        })
        .catch(err => {
          console.error('Avatar upload failed', err);
          NotificationHelper.toastError('Upload mislukt door een onverwachte fout.');
        })
        .finally(() => {
          uploadElement.value = '';
          this.setState({ uploading: false });
        });
    } else {
      NotificationHelper.toastError('Upload mislukt door een onverwachte fout.');
    }
  };

  private async resizeFile(file: File): Promise<Blob> {
    return readAndCompressImage(file, { quality: 0.8, maxWidth: 800, maxHeight: 800, mimeType: 'image/jpeg' });
  }

  render(): ReactNode {
    const { className } = this.props;
    const { avatarUrl, uploading } = this.state;
    const combinedClassName = `change-avatar ${className ? className : ''}`;
    return (
      <Form className={combinedClassName} onSubmit={this.handleClick}>
        <Form.File
          className="change-avatar__file-browser"
          required
          ref={this.fileBrowserRef}
          onChange={this.handleChange}
        />
        <img
          className="change-avatar__avatar"
          src={avatarUrl}
          alt="Avatar van de gebruiker"
          onClick={this.handleClick}
        />
        <div className="change-avatar__change-button-container">
          <Button type="submit" onClick={this.handleClick} disabled={uploading} className="change-avatar__change-button" aria-label="Foto veranderen">
            {
              uploading ?
                <Spinner animation="border" size="sm"/> :
                <Icon icon="edit"/>
            }
          </Button>
        </div>
      </Form>
    );
  }
}

export default ChangeAvatar;
